/**
 * Reusable PO Number Field Component
 * Can be used in both DetailsStep and RmcOrderEdit with consistent styling and behavior
 */

import { useMix } from '@hconnect/tuc-processes/fields'
import { usePoNumber } from '@hconnect/tuc-processes/fields/textField'
import { ControlledInput } from '@hm/fundamental'
import { Box } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Reusable PO Number Field component with consistent styling
 */
export const PoNumberField: React.FC = () => {
  const { t } = useTranslation()
  const { poNumber, setPoNumber, poNumberConfiguration } = usePoNumber()
  const { selectedMaterial } = useMix()
  const lastPrefilledPoNumberRef = useRef<string | undefined>(undefined)
  const maxLength = poNumberConfiguration?.maxLength
  const characterCount = poNumber?.length ?? 0
  const hasReachedMaxLength = Boolean(maxLength && characterCount >= maxLength)

  const isDefaultPoNumber = (value: string) => /^PO-\d{4}-\d{6}$/.test(value)

  const isPoNumberRequired = !poNumber || poNumber.trim().length === 0
  const error = isPoNumberRequired

  const helperText = isPoNumberRequired
    ? t('orderIntake.rmc.steps.shared.validationErrors.poNumberRequired', 'PO Number is required')
    : undefined

  const handlePoNumberChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPoNumber(event.target.value)
    },
    [setPoNumber]
  )

  // Prefill the PO number once when a material's PO number becomes available. Gate on the
  // material's PO number identity (not the live field value) so a user clear/edit is not restored.
  useEffect(() => {
    const materialPoNumber = selectedMaterial?.poNumber
    if (!materialPoNumber) {
      lastPrefilledPoNumberRef.current = undefined
      return
    }
    if (lastPrefilledPoNumberRef.current === materialPoNumber) {
      return
    }
    lastPrefilledPoNumberRef.current = materialPoNumber
    const currentPoNumber = poNumber?.trim() ?? ''
    if (!currentPoNumber || isDefaultPoNumber(currentPoNumber)) {
      setPoNumber(materialPoNumber)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaterial?.poNumber])

  return (
    <>
      <ControlledInput
        name={t('orderIntake.rmc.steps.shared.poNumberLabel', 'PO number')}
        label={t('orderIntake.rmc.steps.shared.poNumberLabel', 'PO number')}
        value={poNumber ?? ''}
        onChange={handlePoNumberChange}
        isDisabled={false}
        maxLength={poNumberConfiguration?.maxLength}
        data-test-id="po-number-input"
        state={error ? 'error' : undefined}
        hint={error ? helperText : undefined} // todo: fix hint not showing in Fundamental Design System
      />
      {maxLength && (
        <Box
          data-test-id="po-number-character-counter"
          sx={{
            color: hasReachedMaxLength ? 'error.main' : '#52667a',
            display: 'flex',
            fontSize: '12px',
            justifyContent: 'flex-start',
            marginLeft: '12px'
          }}
        >
          {t('orderIntake.rmc.steps.shared.poNumberCharacterCount', {
            count: characterCount,
            maxLength
          })}
          {hasReachedMaxLength && (
            <Box component="span" sx={{ marginLeft: '10px' }}>
              {t('orderIntake.rmc.steps.shared.poNumberMaximumLengthError', {
                maxLength
              })}
            </Box>
          )}
        </Box>
      )}
    </>
  )
}
